import { Company, CompanyApiData } from '@/types/Company'
import en from '@/locale/en'
import fr from '@/locale/fr'

/**
 * Get localized label for codes (for display purposes only)
 */
function getLocalizedLabel(code: string, codeType: 'legal_form' | 'ape_naf' | 'employee_count', localeData: any): string {
  const codeMappings = {
    legal_form: localeData.legal_form_codes,
    ape_naf: localeData.ape_naf_codes,
    employee_count: localeData.employee_count_codes
  }
  
  const mapping = codeMappings[codeType]
  return mapping?.[code] ?? code
}

/**
 * Extract latest financial data from API response
 */
function extractLatestFinances(apiData: CompanyApiData) {
  if (!apiData.finances) return null
  
  const latestFinancialYear = Object.keys(apiData.finances).sort((a, b) => a.localeCompare(b)).pop()
  return latestFinancialYear ? apiData.finances[latestFinancialYear] : null
}

/**
 * Extract IDCC code and label from API response
 */
function extractIdccInfo(apiData: CompanyApiData, localeData: any) {
  const idccCode = apiData.complements?.liste_idcc?.[0] ?? undefined
  const idccLabel = idccCode ? getLocalizedLabel(idccCode, 'legal_form', localeData) : undefined
  return { idccCode, idccLabel }
}

/**
 * Create location string from coordinates
 */
function createLocationString(siege: any): string | undefined {
  if (!siege?.latitude || !siege?.longitude) return undefined
  return `POINT(${siege.longitude} ${siege.latitude})`
}

/**
 * Extract manager name from dirigeants
 */
function extractManagerName(dirigeants: any[]): string | undefined {
  if (!dirigeants || dirigeants.length === 0) return undefined
  
  const manager = dirigeants[0]
  const prenoms = manager.prenoms || ''
  const nom = manager.nom
  const qualite = manager.qualite
  
  if (prenoms) {
    return qualite ? `${prenoms} ${nom} (${qualite})` : `${prenoms} ${nom}`
  } else {
    return qualite ? `${nom} (${qualite})` : nom
  }
}

/**
 * Transform API company data to our Company interface
 * @param apiData Raw API company data
 * @param locale Current locale ('en' or 'fr')
 * @returns Transformed Company object
 */
export function transformCompanyData(apiData: CompanyApiData, locale = 'fr'): Company {
  const localeData = locale === 'en' ? en : fr
  
  const latestFinances = extractLatestFinances(apiData)
  const { idccCode, idccLabel } = extractIdccInfo(apiData, localeData)
  const location = createLocationString(apiData.siege)
  const managerName = extractManagerName(apiData.dirigeants || [])

  const company: Company = {
    // Basic identification
    name: apiData.nom_raison_sociale || apiData.nom_complet,
    trade_name: apiData.siege?.nom_commercial,
    complete_name: apiData.nom_complet,
    state: 'prospect', // Default state
    
    // Legal identification
    siret: apiData.siege?.siret,
    siren: apiData.siren,
    category: apiData.categorie_entreprise,
    legal_form: apiData.nature_juridique, // Store the code, not the label
    ape_code: apiData.activite_principale ?? apiData.siege?.activite_principale,
    activity_details: apiData.activite_principale, // Store the code, not the label
    
    // Address information
    address: apiData.siege?.adresse,
    postal_code: apiData.siege?.code_postal,
    city: apiData.siege?.libelle_commune,
    country: 'France', // Default country
    location,
    head_office_address: apiData.siege?.adresse,
    billing_address: apiData.siege?.adresse, // Default to head office address
    
    // Business information
    code_idcc: idccCode,
    code_idcc_label: idccLabel, // Keep this as it's a computed field from code_idcc
    annual_revenue_k: latestFinances?.ca ? Math.round(latestFinances.ca / 1000) : undefined,
    employee_count: apiData.tranche_effectif_salarie, // Store the code, not the label
    
    // Display fields (for UI compatibility)
    manager_name: managerName,
    
    // Flags
    is_foreign: false, // Default to French company
    
    // Timestamps
    created_at: apiData.date_creation,
    updated_at: apiData.date_mise_a_jour
  }

  return company
}

/**
 * Transform multiple API company data to Company interface array
 * @param apiDataArray Array of raw API company data
 * @param locale Current locale ('en' or 'fr')
 * @returns Array of transformed Company objects
 */
export function transformCompanyDataArray(apiDataArray: CompanyApiData[], locale = 'fr'): Company[] {
  return apiDataArray.map(apiData => transformCompanyData(apiData, locale))
} 